import { Mail, ShieldCheck, FileText, Cookie, Scale } from 'lucide-react';

export default function Privacy() {
  const sections = [
    {
      title: 'Overview',
      icon: FileText,
      body: [
        `This Privacy Policy is part of the Terms and Conditions for njjamaat.org and any affiliated NJ Jamaat platforms that use the same name, logo, or branding.`,
        `This policy explains how NJ Jamaat may collect, use, protect, and retain personal information submitted through the website.`,
        `This policy applies only to information collected through njjamaat.org. It does not apply to third-party websites linked from the Site. Those websites are governed by their own privacy policies.`,
      ],
    },
    {
      title: 'Information We May Collect',
      icon: ShieldCheck,
      body: [
        `From time to time, NJ Jamaat may offer projects, programs, forms, registrations, or services that allow you to provide personal information through the Site.`,
        `By submitting personal information, you consent to NJ Jamaat using that information for the purpose for which it was provided. This may include event registration, communication, administrative follow-up, community services, or related Jamaat operations.`,
        `Personal information may be shared with related Jamaat entities or trusted service providers when needed to process requests, provide services, or support website operations.`,
      ],
    },
    {
      title: 'Requests Regarding Personal Information',
      icon: Mail,
      body: [
        `If you have concerns or requests regarding your personal information, you may contact NJ Jamaat at pr@njjamaat.org.`,
        `We will make reasonable efforts to review, amend, or remove information where appropriate. Please note that processing such requests may take some time.`,
      ],
    },
    {
      title: 'Minors',
      icon: ShieldCheck,
      body: [
        `Minors should not submit personal information through the Site without the consent of a parent or legal guardian.`,
        `If NJ Jamaat becomes aware that personal information was submitted by a minor without appropriate consent, we will make reasonable efforts to remove that information.`,
      ],
    },
    {
      title: 'Security',
      icon: ShieldCheck,
      body: [
        `NJ Jamaat uses reasonable technical and organizational measures to help protect personal information against unauthorized access, misuse, loss, or destruction.`,
        `However, no website or online system can guarantee complete security. Users should avoid submitting sensitive information unless it is necessary for the relevant service or request.`,
      ],
    },
    {
      title: 'Legal Requirements',
      icon: Scale,
      body: [
        `NJ Jamaat may disclose personal information when required by law, regulation, legal process, or a good-faith belief that disclosure is necessary to comply with legal obligations.`,
        `Information may also be disclosed when necessary to protect the rights, property, or safety of NJ Jamaat, related entities, website users, or the public.`,
      ],
    },
    {
      title: 'Cookies',
      icon: Cookie,
      body: [
        `The Site may use cookies or similar basic website technologies to support normal website functionality and improve the user experience.`,
        `You may adjust your browser settings to reject cookies if your browser allows it. Some parts of the Site may not function properly if cookies are disabled.`,
      ],
    },
    {
      title: 'Changes to This Policy',
      icon: FileText,
      body: [
        `By using the Site, you acknowledge that you have read this Privacy Policy and consent to the use of information as described above.`,
        `This Privacy Policy may be updated from time to time. Any changes will become effective when posted on njjamaat.org.`,
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div
        className="relative py-20 text-white overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d66 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-64 h-64 rounded-full border-2 border-dawoodi-gold/30"></div>
          <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full border-2 border-dawoodi-gold/30"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-dawoodi-gold to-transparent rounded-full"></div>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold">
              Privacy Policy
            </h1>
          </div>

          <p className="font-poppins text-white/85 max-w-3xl">
            This policy explains how information submitted through njjamaat.org may be collected,
            used, and protected.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-1 h-8 bg-gradient-to-b from-dawoodi-navy to-transparent rounded-full"></div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-dawoodi-navy">
              Website Privacy
            </h2>
          </div>

          <p className="font-poppins text-gray-600 max-w-3xl">
            Please review the following policy before submitting information through this website.
          </p>
        </div>

        <div className="space-y-7">
          {sections.map((section, index) => {
            const Icon = section.icon;

            return (
              <section
                key={section.title}
                className={`group relative transition-all duration-300 ${
                  index % 2 === 1 ? 'lg:ml-6' : 'lg:mr-6'
                }`}
              >
                <div className="relative bg-white border-2 border-dawoodi-gold/20 shadow-lg rounded-xl overflow-hidden hover:border-dawoodi-gold hover:shadow-2xl transition-all duration-300">
                  <div className="h-1 bg-gradient-to-r from-dawoodi-navy via-dawoodi-gold to-transparent"></div>

                  <div className="p-5 md:p-7">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-dawoodi-light/60 border border-dawoodi-gold/30 flex items-center justify-center flex-shrink-0">
                        <Icon size={22} className="text-dawoodi-navy" />
                      </div>

                      <div className="min-w-0">
                        <h3 className="font-playfair font-bold text-xl text-dawoodi-navy">
                          {section.title}
                        </h3>

                        <div className="mt-4 space-y-4">
                          {section.body.map((paragraph, paragraphIndex) => (
                            <p
                              key={paragraphIndex}
                              className="font-poppins text-sm md:text-base text-gray-600 leading-relaxed"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-10 bg-dawoodi-light/40 border-2 border-dawoodi-gold/20 rounded-xl p-5 md:p-7">
          <div className="flex items-start gap-4">
            <Mail size={24} className="text-dawoodi-navy mt-1 flex-shrink-0" />

            <div>
              <h3 className="font-playfair text-xl font-bold text-dawoodi-navy">
                Privacy Questions
              </h3>

              <p className="font-poppins text-gray-600 mt-2">
                For privacy-related questions or personal information requests, contact{' '}
                <a
                  href="mailto:pr@njjamaat.org"
                  className="text-dawoodi-gold hover:underline font-semibold"
                >
                  pr@njjamaat.org
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}